import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Bell, Calendar, Car, Check, ChevronDown, ChevronRight,
  Clock, Droplet, Filter, Gauge, Info, Settings, Wrench, Zap,
  CircleDot, Wind, Cog, Shield, Snowflake, Sparkles, Battery,
  ArrowUpDown, ThermometerSun, X, Plus, HelpCircle, ArrowRight,
  ClipboardList, CheckCircle2, Lightbulb
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { MAINTENANCE_ITEMS, VEHICLE_PRESETS, calculateMaintenanceDue, getUpcomingMaintenance } from '../../lib/maintenanceSchedules'
import { getMakeModelSchedule, getVehicleCategory, SPECIAL_MAINTENANCE } from '../../lib/makeModelSchedules'

// Icon mapping
const iconMap = {
  Droplet, Thermometer: ThermometerSun, Cog, AlertCircle: AlertTriangle, RotateCw: Cog,
  Circle: CircleDot, Disc: CircleDot, CircleDot, Move: ArrowUpDown, Wind, Filter,
  Repeat: Cog, Clock, Link: Cog, Zap, Battery, ArrowUpDown, Snowflake, Sparkles,
  Armchair: Sparkles, Wrench
}

const getIcon = (iconName) => iconMap[iconName] || Wrench

export default function MaintenanceAlerts() {
  const [vehicles, setVehicles] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [maintenanceItems, setMaintenanceItems] = useState([])
  const [serviceHistory, setServiceHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSettings, setShowSettings] = useState(false)
  const [filterUrgency, setFilterUrgency] = useState('all')
  const [expandedCategories, setExpandedCategories] = useState({})
  const [showGettingStarted, setShowGettingStarted] = useState(false)

  useEffect(() => {
    fetchVehicles()
  }, [])

  useEffect(() => {
    if (selectedVehicle) {
      fetchServiceHistory(selectedVehicle.id)
    }
  }, [selectedVehicle])

  useEffect(() => {
    if (selectedVehicle && serviceHistory !== undefined) {
      calculateMaintenance()
    }
  }, [selectedVehicle, serviceHistory])

  async function fetchVehicles() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('owner_id', user.id)
        .order('year', { ascending: false })

      if (error) throw error

      setVehicles(data || [])
      if (data?.length > 0) {
        setSelectedVehicle(data[0])
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error)
    } finally {
      setLoading(false)
    }
  }

  async function fetchServiceHistory(vehicleId) {
    try {
      const { data, error } = await supabase
        .from('service_records')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .order('service_date', { ascending: false })

      if (error) throw error
      setServiceHistory(data || [])
      // Show getting started if no service history
      setShowGettingStarted((!data || data.length === 0))
    } catch (error) {
      console.error('Error fetching service history:', error)
      setServiceHistory([])
      setShowGettingStarted(true)
    }
  }

  function calculateMaintenance() {
    if (!selectedVehicle) return

    const make = selectedVehicle.make
    const model = selectedVehicle.model
    const year = selectedVehicle.year
    const currentMiles = selectedVehicle.mileage || 0
    const hasServiceHistory = serviceHistory && serviceHistory.length > 0

    // Get make/model specific schedule
    const makeModelSchedule = getMakeModelSchedule(make, model)
    const vehicleCategory = getVehicleCategory(year, make)
    const preset = VEHICLE_PRESETS[vehicleCategory] || VEHICLE_PRESETS.modern_daily

    const results = []

    // Process each maintenance item from the preset
    for (const itemKey of preset.items) {
      const baseSchedule = MAINTENANCE_ITEMS[itemKey]
      if (!baseSchedule) continue

      // Override with make/model specific intervals if available
      const makeOverride = makeModelSchedule?.[itemKey]
      const schedule = {
        ...baseSchedule,
        defaultMiles: makeOverride?.miles ?? baseSchedule.defaultMiles,
        defaultMonths: makeOverride?.months ?? baseSchedule.defaultMonths,
      }

      // Find last service for this item
      const lastService = serviceHistory?.find(s => {
        const serviceDesc = s.description?.toLowerCase() || ''
        const serviceType = s.service_type?.toLowerCase() || ''
        const itemName = schedule.name.toLowerCase()

        return serviceDesc.includes(itemName) ||
               serviceType.includes(itemName) ||
               serviceDesc.includes(itemKey.replace(/_/g, ' '))
      })

      // Calculate due status
      const now = new Date()
      let dueMiles = null
      let dueDate = null
      let urgency = 'unknown' // New status for items without history
      let milesRemaining = null
      let daysRemaining = null

      // If we have service history for this item, calculate accurately
      if (lastService) {
        // Miles-based calculation
        if (schedule.defaultMiles && currentMiles > 0) {
          const lastMiles = lastService.mileage || 0
          dueMiles = lastMiles + schedule.defaultMiles
          milesRemaining = dueMiles - currentMiles

          if (milesRemaining < 0) urgency = 'overdue'
          else if (milesRemaining < schedule.defaultMiles * 0.1) urgency = 'due'
          else if (milesRemaining < schedule.defaultMiles * 0.25) urgency = 'soon'
          else urgency = 'ok'
        }

        // Time-based calculation
        if (schedule.defaultMonths) {
          const lastDate = lastService.date ? new Date(lastService.date) : null
          if (lastDate) {
            dueDate = new Date(lastDate)
            dueDate.setMonth(dueDate.getMonth() + schedule.defaultMonths)
            daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24))

            if (daysRemaining < 0 && urgency !== 'overdue') urgency = 'overdue'
            else if (daysRemaining < 30 && (urgency === 'ok' || urgency === 'unknown')) urgency = 'due'
            else if (daysRemaining < 60 && (urgency === 'ok' || urgency === 'unknown')) urgency = 'soon'
            else if (urgency === 'unknown') urgency = 'ok'
          }
        }
      } else {
        // No service history - show as "unknown" (needs baseline) for most items
        urgency = 'unknown'

        // For critical safety items, suggest checking soon
        if (schedule.severity === 'high' || baseSchedule.severity === 'high') {
          urgency = 'check'
        }
      }

      results.push({
        key: itemKey,
        ...schedule,
        dueMiles,
        dueDate,
        urgency,
        milesRemaining,
        daysRemaining,
        lastService,
        makeSpecific: !!makeOverride
      })
    }

    // Add special maintenance items for this make
    const makeLower = make?.toLowerCase()
    const specialItems = SPECIAL_MAINTENANCE[makeLower] || []
    for (const special of specialItems) {
      const hasServiceRecord = serviceHistory?.some(s => {
        const desc = s.description?.toLowerCase() || ''
        return desc.includes(special.name.toLowerCase())
      })

      results.push({
        key: special.id,
        name: special.name,
        category: 'Special',
        icon: 'AlertCircle',
        severity: special.severity,
        description: special.note || '',
        urgency: hasServiceRecord ? 'ok' : (special.severity === 'critical' ? 'check' : 'unknown'),
        makeSpecific: true,
        isSpecial: true,
        lastService: null
      })
    }

    // Sort by urgency (but put unknown at the end, not as overdue)
    const urgencyOrder = { overdue: 0, due: 1, check: 2, soon: 3, ok: 4, unknown: 5 }
    results.sort((a, b) => {
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[a.urgency] - urgencyOrder[b.urgency]
      }
      return (a.daysRemaining || 999) - (b.daysRemaining || 999)
    })

    setMaintenanceItems(results)
  }

  // Group items by category
  const groupedItems = maintenanceItems.reduce((acc, item) => {
    const category = item.category || 'Other'
    if (!acc[category]) acc[category] = []
    acc[category].push(item)
    return acc
  }, {})

  // Filter by urgency
  const filteredItems = filterUrgency === 'all'
    ? maintenanceItems
    : maintenanceItems.filter(item => item.urgency === filterUrgency)

  // Count by urgency
  const urgencyCounts = maintenanceItems.reduce((acc, item) => {
    acc[item.urgency] = (acc[item.urgency] || 0) + 1
    return acc
  }, {})

  // Calculate items that need attention (not unknown)
  const knownItems = maintenanceItems.filter(i => i.urgency !== 'unknown')
  const needsAttention = knownItems.filter(i => ['overdue', 'due', 'check'].includes(i.urgency)).length
  const allGood = knownItems.filter(i => ['ok', 'soon'].includes(i.urgency)).length

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  const getUrgencyStyles = (urgency) => {
    switch (urgency) {
      case 'overdue':
        return 'bg-red-50 border-red-200'
      case 'due':
        return 'bg-amber-50 border-amber-200'
      case 'check':
        return 'bg-purple-50 border-purple-200'
      case 'soon':
        return 'bg-blue-50 border-blue-200'
      case 'unknown':
        return 'bg-rdc-cream/50 border-rdc-stone/30'
      default:
        return 'bg-green-50 border-green-200'
    }
  }

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'overdue':
        return <span className="badge bg-red-500 text-white">Overdue</span>
      case 'due':
        return <span className="badge bg-amber-500 text-white">Due Now</span>
      case 'check':
        return <span className="badge bg-purple-500 text-white">Check Soon</span>
      case 'soon':
        return <span className="badge bg-blue-500 text-white">Coming Up</span>
      case 'unknown':
        return <span className="badge bg-rdc-stone text-white">No History</span>
      default:
        return <span className="badge bg-green-500 text-white">Good</span>
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner" />
      </div>
    )
  }

  if (vehicles.length === 0) {
    return (
      <div className="animate-fade-in">
        <div className="card p-12 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-rdc-cream flex items-center justify-center">
            <Car size={28} className="text-rdc-taupe" strokeWidth={1.5} />
          </div>
          <h2 className="section-title mb-2">No Vehicles Yet</h2>
          <p className="text-body text-rdc-charcoal mb-6">
            Add a vehicle to start tracking maintenance schedules
          </p>
          <Link to="/garage/add" className="btn-primary">
            <Plus size={18} />
            Add Your First Vehicle
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-label mb-1">Smart Reminders</p>
          <h1 className="page-title">Maintenance Alerts</h1>
          <p className="text-body mt-2">
            Personalized schedules based on your {selectedVehicle?.make}'s requirements
          </p>
        </div>
      </div>

      {/* Vehicle Selector */}
      <div className="card p-5 mb-6">
        <label className="text-label mb-3 block">Select Vehicle</label>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {vehicles.map((vehicle) => (
            <button
              key={vehicle.id}
              onClick={() => setSelectedVehicle(vehicle)}
              className={`flex-shrink-0 px-5 py-3.5 rounded-xl border-2 transition-all text-left ${
                selectedVehicle?.id === vehicle.id
                  ? 'border-rdc-orange bg-rdc-orange/5'
                  : 'border-rdc-stone/40 hover:border-rdc-stone'
              }`}
            >
              <div className="text-sm font-medium text-black">
                {vehicle.year} {vehicle.make}
              </div>
              <div className="text-xs text-rdc-taupe">{vehicle.model}</div>
              {vehicle.mileage && (
                <div className="text-xs text-rdc-charcoal mt-1.5 font-medium">
                  {vehicle.mileage.toLocaleString()} miles
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Getting Started Banner - for users without service history */}
      {showGettingStarted && (
        <div className="card p-6 mb-6 bg-gradient-to-br from-rdc-cream via-white to-rdc-cream border-rdc-orange/20">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-rdc-orange/10 flex items-center justify-center flex-shrink-0">
              <Lightbulb size={24} className="text-rdc-orange" />
            </div>
            <div className="flex-1">
              <h3 className="section-title mb-1">Welcome to Smart Maintenance</h3>
              <p className="text-body text-rdc-charcoal mb-4">
                To provide accurate maintenance alerts, we need to know your vehicle's service history.
                Log your most recent services (like oil changes, brake work, etc.) to establish a baseline.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/service-history?vehicle=${selectedVehicle?.id}`}
                  className="btn-primary"
                >
                  <ClipboardList size={16} />
                  Log Service History
                </Link>
                <button
                  onClick={() => setShowGettingStarted(false)}
                  className="btn-ghost text-rdc-taupe"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards - Redesigned for better UX */}
      {!showGettingStarted && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Needs Attention */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-black">{needsAttention}</div>
                <div className="text-xs text-rdc-taupe">Needs Attention</div>
              </div>
            </div>
            {needsAttention > 0 && (
              <button
                onClick={() => setFilterUrgency('due')}
                className="text-xs text-rdc-orange font-medium hover:underline flex items-center gap-1"
              >
                View items <ArrowRight size={12} />
              </button>
            )}
          </div>

          {/* Up to Date */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 size={20} className="text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-black">{allGood}</div>
                <div className="text-xs text-rdc-taupe">Up to Date</div>
              </div>
            </div>
            {allGood > 0 && (
              <button
                onClick={() => setFilterUrgency('ok')}
                className="text-xs text-rdc-charcoal font-medium hover:underline flex items-center gap-1"
              >
                View items <ArrowRight size={12} />
              </button>
            )}
          </div>

          {/* Needs History */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-rdc-cream flex items-center justify-center">
                <HelpCircle size={20} className="text-rdc-taupe" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-black">{urgencyCounts.unknown || 0}</div>
                <div className="text-xs text-rdc-taupe">Needs Baseline</div>
              </div>
            </div>
            {(urgencyCounts.unknown || 0) > 0 && (
              <Link
                to={`/service-history?vehicle=${selectedVehicle?.id}`}
                className="text-xs text-rdc-orange font-medium hover:underline flex items-center gap-1"
              >
                Log history <ArrowRight size={12} />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Quick Filter Pills */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6 -mx-2 px-2">
        {[
          { id: 'all', label: 'All Items', count: maintenanceItems.length },
          { id: 'overdue', label: 'Overdue', count: urgencyCounts.overdue || 0, color: 'red' },
          { id: 'due', label: 'Due Now', count: urgencyCounts.due || 0, color: 'amber' },
          { id: 'check', label: 'Check Soon', count: urgencyCounts.check || 0, color: 'purple' },
          { id: 'soon', label: 'Coming Up', count: urgencyCounts.soon || 0, color: 'blue' },
          { id: 'ok', label: 'Good', count: urgencyCounts.ok || 0, color: 'green' },
        ].map(filter => {
          if (filter.id !== 'all' && filter.count === 0) return null
          return (
            <button
              key={filter.id}
              onClick={() => setFilterUrgency(filter.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                filterUrgency === filter.id
                  ? 'bg-rdc-orange text-white shadow-sm'
                  : 'bg-white text-rdc-charcoal hover:bg-rdc-cream border border-rdc-stone/50'
              }`}
            >
              {filter.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                filterUrgency === filter.id ? 'bg-white/20' : 'bg-rdc-cream'
              }`}>
                {filter.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Make-specific notice */}
      {selectedVehicle && getMakeModelSchedule(selectedVehicle.make, selectedVehicle.model) && (
        <div className="flex items-center gap-3 p-4 bg-rdc-forest/5 border border-rdc-forest/20 rounded-xl mb-6">
          <Info size={18} className="text-rdc-forest flex-shrink-0" />
          <p className="text-sm text-rdc-forest">
            <strong>Optimized for {selectedVehicle.make}</strong> — Using manufacturer-specific maintenance intervals
          </p>
        </div>
      )}

      {/* Maintenance Items by Category */}
      <div className="space-y-4">
        {Object.entries(groupedItems).map(([category, items]) => {
          const categoryItems = filterUrgency === 'all'
            ? items
            : items.filter(item => item.urgency === filterUrgency)

          if (categoryItems.length === 0) return null

          const isExpanded = expandedCategories[category] !== false

          return (
            <div key={category} className="card overflow-hidden">
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between p-4 hover:bg-rdc-cream/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-rdc-cream flex items-center justify-center">
                    <Wrench size={16} className="text-rdc-taupe" />
                  </div>
                  <span className="font-medium text-black">{category}</span>
                  <span className="text-xs text-rdc-taupe bg-rdc-cream px-2 py-0.5 rounded-full">
                    {categoryItems.length} items
                  </span>
                </div>
                <ChevronDown
                  size={18}
                  className={`text-rdc-taupe transition-transform ${isExpanded ? '' : '-rotate-90'}`}
                />
              </button>

              {isExpanded && (
                <div className="border-t border-rdc-stone/20">
                  {categoryItems.map((item) => {
                    const IconComponent = getIcon(item.icon)

                    return (
                      <div
                        key={item.key}
                        className={`p-4 border-b border-rdc-stone/10 last:border-b-0 ${getUrgencyStyles(item.urgency)}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            item.urgency === 'overdue' ? 'bg-red-100 text-red-600' :
                            item.urgency === 'due' ? 'bg-amber-100 text-amber-600' :
                            item.urgency === 'check' ? 'bg-purple-100 text-purple-600' :
                            item.urgency === 'soon' ? 'bg-blue-100 text-blue-600' :
                            item.urgency === 'unknown' ? 'bg-rdc-stone/20 text-rdc-taupe' :
                            'bg-green-100 text-green-600'
                          }`}>
                            <IconComponent size={18} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-medium text-black">{item.name}</h3>
                              {getUrgencyBadge(item.urgency)}
                              {item.makeSpecific && (
                                <span className="badge bg-rdc-forest/10 text-rdc-forest">
                                  {selectedVehicle?.make}
                                </span>
                              )}
                            </div>

                            <p className="text-sm text-rdc-charcoal mb-2">{item.description}</p>

                            {/* Status info - only show if we have data */}
                            {item.urgency !== 'unknown' && (
                              <div className="flex flex-wrap gap-4 text-sm text-rdc-charcoal">
                                {item.milesRemaining !== null && (
                                  <div className="flex items-center gap-1.5">
                                    <Gauge size={14} className="text-rdc-taupe" />
                                    {item.milesRemaining > 0
                                      ? `${item.milesRemaining.toLocaleString()} miles remaining`
                                      : `${Math.abs(item.milesRemaining).toLocaleString()} miles overdue`
                                    }
                                  </div>
                                )}
                                {item.daysRemaining !== null && (
                                  <div className="flex items-center gap-1.5">
                                    <Calendar size={14} className="text-rdc-taupe" />
                                    {item.daysRemaining > 0
                                      ? `${item.daysRemaining} days remaining`
                                      : `${Math.abs(item.daysRemaining)} days overdue`
                                    }
                                  </div>
                                )}
                                {item.lastService && (
                                  <div className="flex items-center gap-1.5 text-rdc-taupe">
                                    <Clock size={14} />
                                    Last: {new Date(item.lastService.date).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Unknown status helper text */}
                            {item.urgency === 'unknown' && (
                              <p className="text-xs text-rdc-taupe italic">
                                Log your last {item.name.toLowerCase()} service to track this item
                              </p>
                            )}

                            {item.tips && (
                              <p className="mt-2 text-xs text-rdc-taupe italic">{item.tips}</p>
                            )}
                          </div>

                          <Link
                            to={`/service-history?vehicle=${selectedVehicle?.id}&add=${item.key}`}
                            className="btn-secondary text-sm flex-shrink-0"
                          >
                            {item.lastService ? 'Log Service' : 'Add History'}
                          </Link>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
            <Check size={28} className="text-green-600" />
          </div>
          <h3 className="section-title mb-2">
            {filterUrgency === 'all' ? 'No Maintenance Items' : `No ${filterUrgency} items`}
          </h3>
          <p className="text-body text-rdc-charcoal">
            {filterUrgency === 'all'
              ? 'Your vehicle maintenance schedule will appear here.'
              : 'Great! Nothing needs attention in this category.'
            }
          </p>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-10 card p-6 bg-rdc-cream/30">
        <h3 className="text-sm font-medium text-black mb-3 flex items-center gap-2">
          <Info size={16} className="text-rdc-taupe" />
          How Maintenance Alerts Work
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-rdc-charcoal">
          <div>
            <strong className="text-black">Personalized Schedules</strong>
            <p className="mt-1">We use manufacturer-specific intervals for your {selectedVehicle?.make} based on best practices and owner community feedback.</p>
          </div>
          <div>
            <strong className="text-black">Service History Tracking</strong>
            <p className="mt-1">Log your services to get accurate "due" notifications. Without history, we can't calculate when items are actually due.</p>
          </div>
          <div>
            <strong className="text-black">Smart Reminders</strong>
            <p className="mt-1">Items are tracked by both mileage and time intervals, so nothing gets forgotten even if the car sits.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
